import { useState } from 'react'
import { Plus, Search, Edit2, Trash2, Copy, History, X, GitBranch } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { getStatusColor, getModuleColor, getPromptTypeColor, formatDate, generateId } from '../utils/helpers'

const emptyPrompt = {
  name: '', module: 'Listing', workflowId: '', stepId: '', version: 'v1.0',
  type: 'System Prompt', content: '', agentIds: [], bindKnowledgeIds: [],
  status: '草稿', updater: '', updatedAt: '', changeNote: ''
}

const modules = ['Listing', 'Ads', 'Visual', 'QA', '通用']
const types = ['System Prompt', 'Input Template', 'Output Template', 'Checker Prompt', 'Addon Rule', 'Workflow Prompt', 'Batch Prompt', 'API Prompt']
const statuses = ['草稿', '测试', '主用', '废弃']

export default function PromptManager() {
  const { prompts, setPrompts, agents, knowledgeBases, canEdit } = useApp()
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState(null)
  const [showDiff, setShowDiff] = useState(null)
  const [form, setForm] = useState(emptyPrompt)

  const filtered = prompts.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.content.toLowerCase().includes(search.toLowerCase())
    const matchType = !filterType || p.type === filterType
    const matchStatus = !filterStatus || p.status === filterStatus
    return matchSearch && matchType && matchStatus
  })

  const openModal = (prompt = null) => {
    if (prompt) { setEditingPrompt(prompt); setForm({ ...prompt }) }
    else { setEditingPrompt(null); setForm({ ...emptyPrompt, id: generateId(), updater: 'Leo', updatedAt: new Date().toISOString() }) }
    setShowModal(true)
  }

  const save = () => {
    if (!form.name || !form.content) return alert('请输入名称和内容')
    const now = new Date().toISOString()
    if (editingPrompt) { setPrompts(prompts.map(p => p.id === editingPrompt.id ? { ...form, updatedAt: now } : p)) }
    else { setPrompts([...prompts, { ...form, id: generateId(), updatedAt: now }]) }
    setShowModal(false)
  }

  const copy = (prompt) => {
    const newPrompt = { ...prompt, id: generateId(), name: prompt.name + ' (复制)', version: prompt.version + '-copy', status: '草稿', updatedAt: new Date().toISOString() }
    setPrompts([...prompts, newPrompt])
  }

  const remove = (id) => { if (!confirm('确定删除？')) return; setPrompts(prompts.filter(p => p.id !== id)) }

  const getAgentNames = (ids) => {
    if (!ids || ids.length === 0) return '-'
    return agents.filter(a => ids.includes(a.id)).map(a => a.name).join(', ')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Prompt 版本中心</h2>
        {canEdit() && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> 新增 Prompt
          </button>
        )}
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="搜索 Prompt..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select w-40" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">全部类型</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="select w-32" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">全部状态</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(prompt => (
          <div key={prompt.id} className="card p-4 card-hover">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold text-slate-800">{prompt.name}</h3>
                  <span className={`badge ${getStatusColor(prompt.status)}`}>{prompt.status}</span>
                  <span className={`badge ${getModuleColor(prompt.module)}`}>{prompt.module}</span>
                  <span className={`badge ${getPromptTypeColor(prompt.type)}`}>{prompt.type}</span>
                  <span className="text-xs text-slate-400">{prompt.version}</span>
                  {prompt.workflowId && <span className="badge bg-purple-100 text-purple-700 text-[10px] flex items-center gap-1"><GitBranch size={10} /> 工作流</span>}
                </div>
                <p className="text-sm text-slate-500 mb-2">适用Agent: {getAgentNames(prompt.agentIds)}</p>
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 max-h-24 overflow-y-auto font-mono text-xs">
                  {prompt.content.substring(0, 200)}{prompt.content.length > 200 ? '...' : ''}
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  <span>更新人: {prompt.updater}</span>
                  <span>更新时间: {formatDate(prompt.updatedAt)}</span>
                  {prompt.changeNote && <span>修改说明: {prompt.changeNote}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-4">
                {canEdit() && (
                  <>
                    <button onClick={() => openModal(prompt)} className="p-1.5 hover:bg-slate-100 rounded" title="编辑"><Edit2 size={14} className="text-slate-500" /></button>
                    <button onClick={() => copy(prompt)} className="p-1.5 hover:bg-slate-100 rounded" title="复制"><Copy size={14} className="text-slate-500" /></button>
                    <button onClick={() => setShowDiff(prompt)} className="p-1.5 hover:bg-slate-100 rounded" title="版本对比"><History size={14} className="text-slate-500" /></button>
                    <button onClick={() => remove(prompt.id)} className="p-1.5 hover:bg-red-50 rounded" title="删除"><Trash2 size={14} className="text-red-500" /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && <div className="card p-8 text-center text-slate-400">暂无数据</div>}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingPrompt ? '编辑 Prompt' : '新增 Prompt'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prompt 名称</label>
                <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">所属模块</label>
                <select className="select" value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}>
                  {modules.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">版本号</label>
                <input className="input" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prompt 类型</label>
                <select className="select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">内容</label>
                <textarea className="textarea h-48 font-mono text-sm" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">状态</label>
                <select className="select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">修改说明</label>
                <input className="input" value={form.changeNote} onChange={e => setForm({ ...form, changeNote: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">绑定知识库</label>
                <select className="select" multiple value={form.bindKnowledgeIds || []} onChange={e => setForm({ ...form, bindKnowledgeIds: Array.from(e.target.selectedOptions, o => o.value) })}>
                  {knowledgeBases.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="btn-secondary">取消</button>
              <button onClick={save} className="btn-primary">保存</button>
            </div>
          </div>
        </div>
      )}

      {showDiff && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">版本对比: {showDiff.name}</h3>
              <button onClick={() => setShowDiff(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="font-medium text-slate-800">{showDiff.version} <span className="text-xs text-primary-600">(当前)</span></div>
                  <div className="text-xs text-slate-500">{formatDate(showDiff.updatedAt)} · {showDiff.updater}</div>
                  <div className="mt-2 text-xs text-slate-600 font-mono bg-white rounded p-2">{showDiff.content.substring(0, 200)}...</div>
                </div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg opacity-60">
                  <div className="font-medium text-slate-800">{showDiff.version.replace(/\d+$/, m => parseInt(m) - 1)}</div>
                  <div className="text-xs text-slate-500">历史版本 (模拟)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
